/**
 * Splash Screen — BonkJS crash-in logo animation.
 *
 * "BONK" letters slam in from above with squash-stretch physics,
 * each impact producing debris particles, shockwaves, and screen shake.
 * "js" slides in from the right, followed by a full Sakurai-style
 * impact burst. The logo settles, holds, then fades out to reveal
 * the title screen.
 *
 * Self-contained: uses its own Graphics-based particles (not the game's
 * ParticleSystem, whose spawn methods are private). Same pattern as
 * MenuRenderer's ambient particles.
 *
 * Design: BonkJS Tweaker logo entrance (squash-stretch, stagger)
 *         Crash Bandicoot crash-in energy (speed, debris, shake)
 *         Sakurai Effects Guide (phased choreography, contrast, end flash)
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { WORLD, SPLASH, Z } from '../constants';
import type { ScreenEffects } from './effects';
import type { ScreenShake } from './screen-shake';

// ─── Self-contained particle ──
interface SplashParticle {
    gfx: Graphics;
    x: number; y: number;
    vx: number; vy: number;
    life: number;
    maxLife: number;
    scale: number;
    gravity: number;
    friction: number;
    rotSpeed: number;
}

// ─── Shockwave ring ──
interface SplashShockwave {
    gfx: Graphics;
    x: number; y: number;
    radius: number;
    maxRadius: number;
    speed: number;
    color: number;
    width: number;
    life: number;
    maxLife: number;
}

// ─── Speed line ──
interface SpeedLine {
    gfx: Graphics;
    x: number; y: number;
    targetX: number; targetY: number;
    speed: number;
    length: number;
    angle: number;
    life: number;
    maxLife: number;
}

// ─── Per-letter animation state ──
interface LetterState {
    text: Text;
    targetY: number;
    currentY: number;
    phase: 'waiting' | 'dropping' | 'impact' | 'bouncing' | 'settling' | 'done';
    timer: number;
    scaleX: number;
    scaleY: number;
    impactFired: boolean;
}

type SplashPhase = 'idle' | 'anticipation' | 'letters' | 'js_slide' | 'burst' | 'settle' | 'hold' | 'fade' | 'done';

export class SplashScreen {
    private container: Container;
    private letterContainer: Container;
    private particleContainer: Container;
    private lineContainer: Container;
    private bgOverlay: Graphics;

    private letters: LetterState[] = [];
    private jsText!: Text;
    private particles: SplashParticle[] = [];
    private shockwaves: SplashShockwave[] = [];
    private speedLines: SpeedLine[] = [];

    private phase: SplashPhase = 'idle';
    private phaseTimer = 0;
    private elapsed = 0;
    private letterIndex = 0;
    private letterStaggerTimer = 0;

    // External systems
    private effects: ScreenEffects;
    private screenShake: ScreenShake;

    // Callback
    private onComplete: (() => void) | null = null;

    // Computed
    private cx: number;
    private logoY: number;
    private jsTargetX = 0;

    constructor(
        uiContainer: Container,
        effects: ScreenEffects,
        screenShake: ScreenShake,
    ) {
        this.effects = effects;
        this.screenShake = screenShake;

        this.cx = WORLD.CANVAS_WIDTH / 2;
        this.logoY = WORLD.CANVAS_HEIGHT * SPLASH.LOGO_Y;

        // Root container — above everything
        this.container = new Container();
        this.container.zIndex = Z.MENU_UI + 100;
        uiContainer.addChild(this.container);

        // Background overlay (solid black)
        this.bgOverlay = new Graphics();
        this.bgOverlay.rect(0, 0, WORLD.CANVAS_WIDTH, WORLD.CANVAS_HEIGHT);
        this.bgOverlay.fill({ color: 0x0a0a15, alpha: 1 });
        this.container.addChild(this.bgOverlay);

        // Particle container (below letters)
        this.particleContainer = new Container();
        this.container.addChild(this.particleContainer);

        // Speed line container
        this.lineContainer = new Container();
        this.container.addChild(this.lineContainer);

        // Letter container (on top)
        this.letterContainer = new Container();
        this.container.addChild(this.letterContainer);
    }

    /** Build letter Text objects. Call AFTER fonts are loaded so metrics are correct. */
    buildLetters(): void {
        const letterStyle = new TextStyle({
            fontFamily: "'Black Ops One', 'Impact', sans-serif",
            fontSize: SPLASH.LETTER_FONT_SIZE,
            fontWeight: 'normal', // Black Ops One only has one weight
            fill: SPLASH.ACCENT,
            stroke: { color: SPLASH.ACCENT_DARK, width: 4 },
            dropShadow: {
                alpha: 0.6,
                angle: Math.PI / 2,
                blur: 12,
                color: SPLASH.ACCENT_GLOW,
                distance: 0,
            },
        });

        const bonk = 'BONK';
        const letterWidths: number[] = [];
        const tempTexts: Text[] = [];

        for (let i = 0; i < bonk.length; i++) {
            const t = new Text({ text: bonk[i], style: letterStyle });
            t.anchor.set(0.5);
            letterWidths.push(t.width);
            tempTexts.push(t);
        }

        const totalWidth = letterWidths.reduce((a, b) => a + b, 0) +
            SPLASH.LETTER_SPACING * (bonk.length - 1);
        const baseX = this.cx - totalWidth / 2;

        let xOffset = 0;
        for (let i = 0; i < bonk.length; i++) {
            const t = tempTexts[i];
            const x = baseX + xOffset + letterWidths[i] / 2;
            t.position.set(x, -200);
            t.alpha = 0;
            this.letterContainer.addChild(t);

            this.letters.push({
                text: t,
                targetY: this.logoY,
                currentY: -200,
                phase: 'waiting',
                timer: 0,
                scaleX: 1,
                scaleY: 1,
                impactFired: false,
            });
            xOffset += letterWidths[i] + SPLASH.LETTER_SPACING;
        }

        // "js" text — lighter weight, smaller
        const jsStyle = new TextStyle({
            fontFamily: "'Black Ops One', 'Impact', sans-serif",
            fontSize: SPLASH.JS_FONT_SIZE,
            fontWeight: 'normal',
            fill: SPLASH.JS_COLOR,
            dropShadow: {
                alpha: 0.3,
                angle: Math.PI / 2,
                blur: 6,
                color: SPLASH.JS_COLOR,
                distance: 0,
            },
        });
        this.jsText = new Text({ text: 'js', style: jsStyle });
        this.jsText.anchor.set(0, 0.5);

        const lastLetter = this.letters[this.letters.length - 1];
        const lastRight = lastLetter.text.x + letterWidths[letterWidths.length - 1] / 2 + 4;
        this.jsTargetX = lastRight;
        this.jsText.position.set(lastRight + 100, this.logoY + SPLASH.LETTER_FONT_SIZE * 0.15);
        this.jsText.alpha = 0;
        this.letterContainer.addChild(this.jsText);
    }

    /** Start the animation. */
    start(onComplete: () => void): void {
        this.onComplete = onComplete;
        this.phase = 'anticipation';
        this.phaseTimer = 0;
        this.elapsed = 0;
        this.letterIndex = 0;
        this.letterStaggerTimer = 0;
        this.spawnSpeedLines();
    }

    /** Per-frame update (variable dt in seconds). */
    update(dt: number): void {
        if (this.phase === 'idle' || this.phase === 'done') return;

        this.elapsed += dt;
        this.phaseTimer += dt;

        switch (this.phase) {
            case 'anticipation': this.updateAnticipation(dt); break;
            case 'letters': this.updateLetters(dt); break;
            case 'js_slide': this.updateJsSlide(dt); break;
            case 'burst': this.updateBurst(dt); break;
            case 'settle': this.updateSettle(dt); break;
            case 'hold': this.updateHold(dt); break;
            case 'fade': this.updateFade(dt); break;
        }

        this.updateParticles(dt);
        this.updateShockwaves(dt);
        this.updateSpeedLines(dt);
    }

    /** Clean up. */
    destroy(): void {
        this.container.destroy({ children: true });
    }

    // ═══════════════════════════════════════════════════════
    //                   PARTICLE SYSTEM
    // ═══════════════════════════════════════════════════════

    private spawnParticle(
        x: number, y: number,
        vx: number, vy: number,
        life: number,
        scale: number,
        color: number,
        gravity: number = 0,
        friction: number = 1,
        add: boolean = true,
    ): void {
        const gfx = new Graphics();
        // Draw a small bright square (crisp — Sakurai)
        const half = 4;
        gfx.rect(-half, -half, half * 2, half * 2);
        gfx.fill({ color, alpha: 1 });
        if (add) gfx.blendMode = 'add';
        gfx.position.set(x, y);
        gfx.scale.set(scale);
        this.particleContainer.addChild(gfx);

        this.particles.push({
            gfx, x, y, vx, vy,
            life, maxLife: life,
            scale, gravity, friction,
            rotSpeed: (Math.random() - 0.5) * 8,
        });
    }

    private spawnSoftParticle(
        x: number, y: number,
        vx: number, vy: number,
        life: number,
        scale: number,
        color: number,
        add: boolean = true,
    ): void {
        const gfx = new Graphics();
        // Soft circle (diffuse glow)
        gfx.circle(0, 0, 6);
        gfx.fill({ color, alpha: 0.7 });
        if (add) gfx.blendMode = 'add';
        gfx.position.set(x, y);
        gfx.scale.set(scale);
        this.particleContainer.addChild(gfx);

        this.particles.push({
            gfx, x, y, vx, vy,
            life, maxLife: life,
            scale, gravity: 0, friction: 0.97,
            rotSpeed: 0,
        });
    }

    private updateParticles(dt: number): void {
        for (let i = this.particles.length - 1; i >= 0; i--) {
            const p = this.particles[i];
            p.life -= dt;

            if (p.life <= 0) {
                p.gfx.destroy();
                this.particles.splice(i, 1);
                continue;
            }

            p.vy += p.gravity;
            p.vx *= p.friction;
            p.vy *= p.friction;
            p.x += p.vx;
            p.y += p.vy;
            p.gfx.position.set(p.x, p.y);
            p.gfx.rotation += p.rotSpeed * dt;

            const t = p.life / p.maxLife;
            p.gfx.alpha = t;                       // fade out
            p.gfx.scale.set(p.scale * (0.3 + 0.7 * t)); // shrink
        }
    }

    private spawnShockwave(
        x: number, y: number,
        maxRadius: number,
        speed: number,
        color: number,
        width: number,
        life: number,
    ): void {
        const gfx = new Graphics();
        gfx.visible = false; // hidden until first draw — prevents stray dot at (0,0)
        this.particleContainer.addChild(gfx);

        this.shockwaves.push({
            gfx, x, y,
            radius: 0, maxRadius, speed,
            color, width, life, maxLife: life,
        });
    }

    private updateShockwaves(dt: number): void {
        for (let i = this.shockwaves.length - 1; i >= 0; i--) {
            const sw = this.shockwaves[i];
            sw.life -= dt;

            if (sw.life <= 0) {
                sw.gfx.destroy();
                this.shockwaves.splice(i, 1);
                continue;
            }

            sw.radius += sw.speed * dt;
            const t = sw.life / sw.maxLife;

            sw.gfx.visible = true;
            sw.gfx.clear();
            sw.gfx.circle(sw.x, sw.y, Math.max(1, sw.radius));
            sw.gfx.stroke({ color: sw.color, width: sw.width * t, alpha: t * 0.8 });
        }
    }

    // ═══════════════════════════════════════════════════════
    //                PHASE: ANTICIPATION
    // ═══════════════════════════════════════════════════════

    private spawnSpeedLines(): void {
        for (let i = 0; i < SPLASH.SPEED_LINE_COUNT; i++) {
            const gfx = new Graphics();
            this.lineContainer.addChild(gfx);

            const angle = (i / SPLASH.SPEED_LINE_COUNT) * Math.PI * 2 + (Math.random() - 0.5) * 0.4;
            const dist = 350 + Math.random() * 250;
            const x = this.cx + Math.cos(angle) * dist;
            const y = this.logoY + Math.sin(angle) * dist;
            const length = SPLASH.SPEED_LINE_LENGTH_MIN +
                Math.random() * (SPLASH.SPEED_LINE_LENGTH_MAX - SPLASH.SPEED_LINE_LENGTH_MIN);

            this.speedLines.push({
                gfx, x, y,
                targetX: this.cx, targetY: this.logoY,
                speed: SPLASH.SPEED_LINE_SPEED * (0.8 + Math.random() * 0.4),
                length, angle,
                life: SPLASH.SPEED_LINE_LIFE + Math.random() * 0.1,
                maxLife: SPLASH.SPEED_LINE_LIFE + Math.random() * 0.1,
            });
        }
    }

    private updateSpeedLines(dt: number): void {
        for (let i = this.speedLines.length - 1; i >= 0; i--) {
            const line = this.speedLines[i];
            line.life -= dt;

            if (line.life <= 0) {
                line.gfx.destroy();
                this.speedLines.splice(i, 1);
                continue;
            }

            // Move toward center
            const dx = line.targetX - line.x;
            const dy = line.targetY - line.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist > 2) {
                line.x += (dx / dist) * line.speed * dt;
                line.y += (dy / dist) * line.speed * dt;
            }

            const alpha = Math.min(1, line.life / line.maxLife) * 0.7;
            const tailAngle = line.angle + Math.PI;
            const endX = line.x + Math.cos(tailAngle) * line.length;
            const endY = line.y + Math.sin(tailAngle) * line.length;

            line.gfx.clear();
            // Dark backing
            line.gfx.moveTo(line.x + 1, line.y + 1);
            line.gfx.lineTo(endX + 1, endY + 1);
            line.gfx.stroke({ color: SPLASH.FLASH_DARK, width: SPLASH.SPEED_LINE_WIDTH + 2, alpha: alpha * 0.4 });
            // Bright line
            line.gfx.moveTo(line.x, line.y);
            line.gfx.lineTo(endX, endY);
            line.gfx.stroke({ color: SPLASH.ACCENT, width: SPLASH.SPEED_LINE_WIDTH, alpha });
        }
    }

    private updateAnticipation(_dt: number): void {
        if (this.phaseTimer >= SPLASH.PHASE0_DURATION) {
            this.phase = 'letters';
            this.phaseTimer = 0;
            this.dropNextLetter();
        }
    }

    // ═══════════════════════════════════════════════════════
    //                  PHASE: LETTERS
    // ═══════════════════════════════════════════════════════

    private dropNextLetter(): void {
        if (this.letterIndex >= this.letters.length) return;
        const letter = this.letters[this.letterIndex];
        letter.phase = 'dropping';
        letter.currentY = -200;
        letter.text.alpha = 1;
        this.letterIndex++;
        this.letterStaggerTimer = 0;
    }

    private updateLetters(dt: number): void {
        // Stagger next letter
        if (this.letterIndex < this.letters.length) {
            this.letterStaggerTimer += dt;
            if (this.letterStaggerTimer >= SPLASH.LETTER_STAGGER) {
                this.dropNextLetter();
            }
        }

        let allDone = true;

        for (const letter of this.letters) {
            if (letter.phase === 'waiting') { allDone = false; continue; }
            if (letter.phase === 'done') continue;
            allDone = false;

            switch (letter.phase) {
                case 'dropping': {
                    letter.currentY += SPLASH.LETTER_DROP_SPEED * dt;
                    // Stretch vertically while falling (motion smear)
                    const approachT = Math.min(1, Math.max(0,
                        (letter.currentY + 200) / (letter.targetY + 200 + SPLASH.LETTER_OVERSHOOT)));
                    letter.scaleX = 1 - approachT * 0.25;
                    letter.scaleY = 1 + approachT * 0.6;

                    if (letter.currentY >= letter.targetY + SPLASH.LETTER_OVERSHOOT) {
                        letter.currentY = letter.targetY + SPLASH.LETTER_OVERSHOOT;
                        letter.phase = 'impact';
                        letter.timer = 0;
                        this.fireLetterImpact(letter);
                    }
                    break;
                }
                case 'impact': {
                    letter.timer += dt;
                    // Squash (BonkJS style: wide + flat)
                    const iT = Math.min(1, letter.timer / 0.05);
                    letter.scaleX = 1 + 0.4 * (1 - iT);  // wide → 1
                    letter.scaleY = 0.65 + 0.35 * iT;      // flat → 1

                    if (letter.timer >= 0.05) {
                        letter.phase = 'bouncing';
                        letter.timer = 0;
                    }
                    break;
                }
                case 'bouncing': {
                    letter.timer += dt;
                    const bT = Math.min(1, letter.timer / 0.1);
                    const ease = 1 - (1 - bT) * (1 - bT);
                    letter.currentY = (letter.targetY + SPLASH.LETTER_OVERSHOOT) -
                        SPLASH.LETTER_BOUNCE_HEIGHT * Math.sin(ease * Math.PI);
                    // Inverse squash during bounce
                    letter.scaleX = 0.88 + 0.12 * bT;
                    letter.scaleY = 1.12 - 0.12 * bT;

                    if (bT >= 1) {
                        letter.phase = 'settling';
                        letter.timer = 0;
                    }
                    break;
                }
                case 'settling': {
                    letter.timer += dt;
                    const sT = Math.min(1, letter.timer / 0.12);
                    // Spring settle
                    const spring = 1 + Math.sin(sT * Math.PI * 2.5) * 0.04 * (1 - sT);
                    letter.currentY = letter.targetY;
                    letter.scaleX = spring;
                    letter.scaleY = 2 - spring;

                    if (sT >= 1) {
                        letter.phase = 'done';
                        letter.scaleX = 1;
                        letter.scaleY = 1;
                        letter.currentY = letter.targetY;
                    }
                    break;
                }
            }

            letter.text.position.y = letter.currentY;
            letter.text.scale.set(letter.scaleX, letter.scaleY);
        }

        if (allDone || this.letters.every(l => l.phase === 'done')) {
            this.phase = 'js_slide';
            this.phaseTimer = 0;
        }
    }

    private fireLetterImpact(letter: LetterState): void {
        if (letter.impactFired) return;
        letter.impactFired = true;

        const x = letter.text.x;
        const y = letter.targetY;

        // ─── Screen shake (heavy!) ───
        this.screenShake.trigger(SPLASH.LETTER_SHAKE, SPLASH.LETTER_SHAKE_DECAY);

        // ─── Screen flash ───
        this.effects.triggerFlash(SPLASH.ACCENT, SPLASH.LETTER_FLASH_ALPHA);

        // ─── Bright debris (upward fan — BIG) ───
        for (let i = 0; i < SPLASH.IMPACT_DEBRIS_COUNT; i++) {
            const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.5;
            const speed = SPLASH.IMPACT_DEBRIS_SPEED_MIN +
                Math.random() * (SPLASH.IMPACT_DEBRIS_SPEED_MAX - SPLASH.IMPACT_DEBRIS_SPEED_MIN);
            const scale = SPLASH.IMPACT_DEBRIS_SCALE_MIN +
                Math.random() * (SPLASH.IMPACT_DEBRIS_SCALE_MAX - SPLASH.IMPACT_DEBRIS_SCALE_MIN);
            const life = SPLASH.IMPACT_DEBRIS_LIFE_MIN +
                Math.random() * (SPLASH.IMPACT_DEBRIS_LIFE_MAX - SPLASH.IMPACT_DEBRIS_LIFE_MIN);

            const color = Math.random() > 0.3 ? SPLASH.ACCENT : SPLASH.ACCENT_GLOW;
            this.spawnParticle(
                x + (Math.random() - 0.5) * 25, y + (Math.random() - 0.5) * 10,
                Math.cos(angle) * speed, Math.sin(angle) * speed,
                life, scale, color, SPLASH.IMPACT_DEBRIS_GRAVITY, 0.96, true,
            );
        }

        // ─── Downward debris (ground spray) ───
        for (let i = 0; i < 6; i++) {
            const angle = Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.6;
            const speed = 3 + Math.random() * 6;
            this.spawnParticle(
                x + (Math.random() - 0.5) * 15, y + 10,
                Math.cos(angle) * speed, Math.sin(angle) * speed,
                0.15 + Math.random() * 0.15, 0.2 + Math.random() * 0.25,
                SPLASH.ACCENT_DARK, 0.3, 0.95, false,
            );
        }

        // ─── Dark contrast particles (Sakurai) ───
        for (let i = 0; i < SPLASH.IMPACT_DARK_COUNT; i++) {
            const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI;
            const speed = 2 + Math.random() * 4;
            this.spawnSoftParticle(
                x + (Math.random() - 0.5) * 12, y,
                Math.cos(angle) * speed, Math.sin(angle) * speed,
                0.15 + Math.random() * 0.1, 2.5 + Math.random() * 2.5,
                SPLASH.FLASH_DARK, false,
            );
        }

        // ─── Flash particles (bright center pops — MORE) ───
        for (let i = 0; i < SPLASH.IMPACT_FLASH_COUNT; i++) {
            this.spawnSoftParticle(
                x + (Math.random() - 0.5) * 8, y + (Math.random() - 0.5) * 8,
                (Math.random() - 0.5) * 1.0, (Math.random() - 0.5) * 1.0,
                0.06 + Math.random() * 0.06, 3.5 + Math.random() * 3,
                0xffffff, true,
            );
        }

        // ─── Horizontal ground crack lines (Sakurai impact lines) ───
        for (let i = 0; i < 3; i++) {
            const dir = (Math.random() > 0.5 ? 1 : -1);
            const speed = 8 + Math.random() * 12;
            this.spawnParticle(
                x + dir * (Math.random() * 8), y + 5,
                dir * speed, (Math.random() - 0.5) * 1.5,
                0.12 + Math.random() * 0.08, 0.15, SPLASH.ACCENT_GLOW, 0, 0.98, true,
            );
        }

        // ─── Shockwave ring ───
        this.spawnShockwave(x, y,
            SPLASH.IMPACT_SHOCKWAVE_RADIUS, SPLASH.IMPACT_SHOCKWAVE_SPEED,
            SPLASH.ACCENT, 4, SPLASH.IMPACT_SHOCKWAVE_LIFE,
        );

        // ─── White snap ring (inner, fast) ───
        this.spawnShockwave(x, y,
            SPLASH.IMPACT_SHOCKWAVE_RADIUS * 0.4, SPLASH.IMPACT_SHOCKWAVE_SPEED * 1.8,
            0xffffff, 2, 0.08,
        );

        // ─── Dark shockwave (contrast) ───
        this.spawnShockwave(x, y,
            SPLASH.IMPACT_SHOCKWAVE_RADIUS * 0.8, SPLASH.IMPACT_SHOCKWAVE_SPEED * 0.6,
            SPLASH.FLASH_DARK, 6, SPLASH.IMPACT_SHOCKWAVE_LIFE * 0.7,
        );
    }

    // ═══════════════════════════════════════════════════════
    //                  PHASE: JS SLIDE
    // ═══════════════════════════════════════════════════════

    private updateJsSlide(_dt: number): void {
        if (this.phaseTimer < SPLASH.JS_DELAY) return;

        const slideT = Math.min(1, (this.phaseTimer - SPLASH.JS_DELAY) / SPLASH.JS_SLIDE_DURATION);
        // BonkJS spring ease (overshoot)
        const ease = springEase(slideT);

        this.jsText.alpha = Math.min(1, slideT * 4);
        this.jsText.position.x = this.jsTargetX + 80 * (1 - ease);

        if (slideT >= 1) {
            this.phase = 'burst';
            this.phaseTimer = 0;
        }
    }

    // ═══════════════════════════════════════════════════════
    //                   PHASE: BURST
    // ═══════════════════════════════════════════════════════

    private burstFired = false;

    private updateBurst(_dt: number): void {
        if (this.phaseTimer >= SPLASH.BURST_DELAY && !this.burstFired) {
            this.burstFired = true;
            this.fireBurst();
        }

        if (this.phaseTimer >= SPLASH.BURST_DELAY + SPLASH.BURST_DURATION) {
            this.phase = 'settle';
            this.phaseTimer = 0;
        }
    }

    private fireBurst(): void {
        const x = this.cx;
        const y = this.logoY;

        // Strong shake
        this.screenShake.trigger(SPLASH.BURST_SHAKE, SPLASH.BURST_SHAKE_DECAY);
        this.effects.triggerFlash(SPLASH.ACCENT, SPLASH.BURST_FLASH_ALPHA);

        // ─── 3-wave particle burst (Sakurai stagger) ───
        for (let wave = 0; wave < 3; wave++) {
            const delay = wave * 30; // 30ms between waves
            setTimeout(() => this.spawnBurstWave(x, y, wave), delay);
        }

        // ─── Radial speed streaks (manga action lines) ───
        for (let i = 0; i < SPLASH.BURST_STREAK_COUNT; i++) {
            const angle = (i / SPLASH.BURST_STREAK_COUNT) * Math.PI * 2;
            const speed = SPLASH.BURST_STREAK_SPEED_MIN +
                Math.random() * (SPLASH.BURST_STREAK_SPEED_MAX - SPLASH.BURST_STREAK_SPEED_MIN);

            this.spawnParticle(
                x + Math.cos(angle) * 8, y + Math.sin(angle) * 8,
                Math.cos(angle) * speed, Math.sin(angle) * speed,
                SPLASH.BURST_STREAK_LIFE, 0.2, 0xffffff, 0, 1, true,
            );
        }

        // ─── Outer crown ring ───
        for (let i = 0; i < SPLASH.BURST_CROWN_COUNT; i++) {
            const angle = (i / SPLASH.BURST_CROWN_COUNT) * Math.PI * 2;
            const speed = 14 + Math.random() * 6;

            this.spawnParticle(
                x + Math.cos(angle) * 12, y + Math.sin(angle) * 12,
                Math.cos(angle) * speed, Math.sin(angle) * speed,
                0.25 + Math.random() * 0.15, 0.4 + Math.random() * 0.3,
                SPLASH.ACCENT_GLOW, 0.08, 0.97, true,
            );
        }

        // ─── Gem particles with spiral tangential velocity ───
        for (let i = 0; i < SPLASH.BURST_GEM_COUNT; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = 5 + Math.random() * 12;
            const tang = (Math.random() > 0.5 ? 1 : -1) * (2 + Math.random() * 4);

            const color = Math.random() > 0.5 ? SPLASH.ACCENT : SPLASH.ACCENT_GLOW;
            this.spawnParticle(
                x + Math.cos(angle) * 6, y + Math.sin(angle) * 6,
                Math.cos(angle) * speed + Math.cos(angle + Math.PI / 2) * tang,
                Math.sin(angle) * speed + Math.sin(angle + Math.PI / 2) * tang,
                0.3 + Math.random() * 0.4, 0.25 + Math.random() * 0.35,
                color, 0.2 + Math.random() * 0.15, 0.98, true,
            );
        }

        // ─── Dark silhouette (Sakurai cavity) ───
        for (let i = 0; i < 5; i++) {
            this.spawnSoftParticle(
                x + (Math.random() - 0.5) * 15, y + (Math.random() - 0.5) * 15,
                (Math.random() - 0.5) * 3, (Math.random() - 0.5) * 3,
                0.12 + Math.random() * 0.08, 3 + Math.random() * 2,
                SPLASH.FLASH_DARK, false,
            );
        }

        // ─── Shockwaves ───
        // Main
        this.spawnShockwave(x, y,
            SPLASH.BURST_SHOCKWAVE_RADIUS, SPLASH.BURST_SHOCKWAVE_SPEED,
            SPLASH.ACCENT, 5, SPLASH.BURST_SHOCKWAVE_LIFE);
        // Dark contrast
        this.spawnShockwave(x, y,
            SPLASH.BURST_SHOCKWAVE_RADIUS * 0.7, SPLASH.BURST_SHOCKWAVE_SPEED * 0.8,
            SPLASH.ACCENT_DARK, 8, SPLASH.BURST_SHOCKWAVE_LIFE * 0.8);
        // White snap ring
        this.spawnShockwave(x, y,
            SPLASH.BURST_SHOCKWAVE_RADIUS * 0.4, SPLASH.BURST_SHOCKWAVE_SPEED * 1.5,
            0xffffff, 2, 0.1);
    }

    private spawnBurstWave(x: number, y: number, wave: number): void {
        const count = Math.floor(SPLASH.BURST_PARTICLE_COUNT / 3);
        for (let i = 0; i < count; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = SPLASH.BURST_SPEED_MIN +
                Math.random() * (SPLASH.BURST_SPEED_MAX - SPLASH.BURST_SPEED_MIN);
            const scale = SPLASH.BURST_SCALE_MIN +
                Math.random() * (SPLASH.BURST_SCALE_MAX - SPLASH.BURST_SCALE_MIN);
            const life = SPLASH.BURST_LIFE_MIN +
                Math.random() * (SPLASH.BURST_LIFE_MAX - SPLASH.BURST_LIFE_MIN);

            const offset = 5 + Math.random() * 12;
            const color = wave === 0 ? 0xffffff :
                Math.random() > 0.15 ? SPLASH.ACCENT : SPLASH.ACCENT_GLOW;
            const add = Math.random() > 0.15;

            this.spawnParticle(
                x + Math.cos(angle) * offset, y + Math.sin(angle) * offset,
                Math.cos(angle) * speed, Math.sin(angle) * speed,
                life, scale, color, 0.05, 0.96, add,
            );
        }
    }

    // ═══════════════════════════════════════════════════════
    //               PHASE: SETTLE + END FLASH
    // ═══════════════════════════════════════════════════════

    private endFlashFired = false;

    private updateSettle(_dt: number): void {
        // Gentle float on letters
        for (const letter of this.letters) {
            letter.text.position.y = letter.targetY +
                Math.sin(this.elapsed * 2.5 + letter.text.x * 0.008) * 2;
        }

        // End flash at 60% through settle
        if (this.phaseTimer >= SPLASH.SETTLE_DURATION * 0.6 && !this.endFlashFired) {
            this.endFlashFired = true;
            this.fireEndFlash();
        }

        if (this.phaseTimer >= SPLASH.SETTLE_DURATION) {
            this.phase = 'hold';
            this.phaseTimer = 0;
        }
    }

    private fireEndFlash(): void {
        this.effects.triggerFlash(SPLASH.ACCENT_GLOW, 0.06);
    }

    // ═══════════════════════════════════════════════════════
    //                    PHASE: HOLD
    // ═══════════════════════════════════════════════════════

    private updateHold(_dt: number): void {
        for (const letter of this.letters) {
            letter.text.position.y = letter.targetY +
                Math.sin(this.elapsed * 2.5 + letter.text.x * 0.008) * 2;
        }

        if (this.phaseTimer >= SPLASH.HOLD_DURATION) {
            this.phase = 'fade';
            this.phaseTimer = 0;
        }
    }

    // ═══════════════════════════════════════════════════════
    //                    PHASE: FADE
    // ═══════════════════════════════════════════════════════

    private updateFade(_dt: number): void {
        const fadeT = Math.min(1, this.phaseTimer / SPLASH.FADE_DURATION);

        this.letterContainer.alpha = 1 - fadeT;
        this.bgOverlay.alpha = 1 - fadeT;
        this.lineContainer.alpha = 1 - fadeT;
        this.particleContainer.alpha = 1 - fadeT;

        if (fadeT >= 1) {
            this.phase = 'done';
            this.container.visible = false;
            this.onComplete?.();
        }
    }
}

// ─── Utility: Spring ease with overshoot ──
function springEase(t: number): number {
    // Approximation of cubic-bezier(0.34, 1.56, 0.64, 1) — BonkJS spring
    const c4 = (2 * Math.PI) / 4.5;
    if (t === 0 || t === 1) return t;
    return 1 - Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) * 0.3 +
        (1 - Math.pow(1 - t, 3)); // blend with ease-out cubic
}
